import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { PublicPageControls } from '@/components/app/PublicPageControls'
import { PublicFooter } from '@/components/app/PublicFooter'

interface AuthSplitShellProps {
  children: ReactNode
  contentClassName?: string
}

export function AuthSplitShell({ children, contentClassName }: AuthSplitShellProps) {
  const { t } = useTranslation()

  return (
    <main className="relative h-dvh w-screen overflow-hidden bg-background">
      <div className="absolute right-4 top-4 z-20 sm:right-6 sm:top-6">
        <PublicPageControls />
      </div>
      <section className="grid min-h-dvh w-full overflow-hidden bg-card text-card-foreground md:grid-cols-[40fr_60fr]">
        <aside className="relative hidden min-h-dvh overflow-hidden md:flex">
          <img
            src="/Uploads/login-bg.avif"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          
          <div className="relative z-10 flex h-full flex-col justify-between p-8 lg:p-10">
            <img
              src="/Uploads/logo-dark.svg"
              alt="HD Parrainage"
              className="h-12 w-auto self-start"
            />

            <div className="max-w-sm space-y-4 text-white">
              <h2 className="text-3xl font-semibold leading-tight lg:text-[2.2rem]">
                {t('auth.loginAsideTitle')}
              </h2>
            </div>
          </div>
        </aside>

        <div
          className={cn(
            'flex min-h-dvh flex-col px-6 py-8 sm:px-8 sm:py-10 md:px-12 lg:px-16 [&_[data-slot=button]]:rounded-[8px] [&_[data-slot=button]]:shadow-none [&_[data-slot=button].w-full]:h-10 [&_[data-slot=button].w-full]:px-4 [&_form_[data-slot=button]]:h-10 [&_form_[data-slot=button]]:px-4 [&_input[data-slot=input]]:h-10 [&_input[data-slot=input]]:rounded-[8px]',
            contentClassName,
          )}
        >
          <div className="flex-1 flex flex-col justify-center">
            <div className="mx-auto flex w-full max-w-[480px] flex-col justify-center">
              {children}
            </div>
          </div>
          <PublicFooter />
        </div>
      </section>
    </main>
  )
}

export function AuthSplitShellSkeleton() {
  return (
    <main className="relative h-dvh w-screen overflow-hidden bg-background">
      <div className="absolute right-4 top-4 z-20 inline-flex items-center gap-1 sm:right-6 sm:top-6">
        <Skeleton className="h-8 w-8 rounded-[8px]" />
        <Skeleton className="h-8 w-8 rounded-[8px]" />
      </div>
      <section className="grid min-h-dvh w-full overflow-hidden bg-card text-card-foreground md:grid-cols-[40fr_60fr]">
        <aside className="relative hidden min-h-dvh overflow-hidden md:flex">
          <img
            src="/Uploads/login-bg.avif"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div className="relative z-10 flex h-full flex-col justify-between p-8 lg:p-10">
            <img src="/Uploads/logo-dark.svg" alt="HD Parrainage" className="h-12 w-auto self-start" />

            <div className="max-w-sm space-y-4">
              <Skeleton className="h-12 w-64 bg-white/20" />
              <Skeleton className="h-12 w-52 bg-white/15" />
            </div>
          </div>
        </aside>

        <div className="flex min-h-dvh flex-col justify-center px-6 py-8 sm:px-8 sm:py-10 md:px-12 lg:px-16">
          <div className="mx-auto w-full max-w-[480px] space-y-8">
              <div className="space-y-2">
                <Skeleton className="h-8 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>

              <div className="space-y-5">
                <div className="space-y-2">
                  <Skeleton className="h-4 w-16" />
                  <Skeleton className="h-10 w-full rounded-[8px]" />
                </div>

                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-10 w-full rounded-[8px]" />
                </div>

                <Skeleton className="h-4 w-36" />
                <Skeleton className="h-10 w-full rounded-[8px]" />


              </div>
          </div>
        </div>
      </section>
    </main>
  )
}
